import { createFileRoute } from '@tanstack/react-router';
import { Alert, Group, Progress, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { AlertTriangle, DollarSign, PieChart, TrendingUp, Wallet } from 'lucide-react';
import { Suspense, lazy, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Badge, Card, PageContainer, PageHeader, Section, Skeleton } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { useCostSnapshot, useOrgCosts } from '../hooks/useCost';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { Table, type TableColumn } from '../components/ui';
import { colors } from '../theme/tokens';

const CostCharts = lazy(() => import('../components/monitoring/CostCharts'));

type RangeKind = '30d' | '90d' | 'prev-month' | 'custom';

const fmtDate = (dt: Date) =>
  `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

/** Map the selected range to Cost Explorer query params (end-exclusive). */
function rangeToParams(kind: RangeKind, start: string, end: string): Record<string, string> {
  switch (kind) {
    case '30d':
      return { days: '30' };
    case '90d':
      return { days: '90' };
    case 'prev-month': {
      const now = new Date();
      return {
        start: fmtDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: fmtDate(new Date(now.getFullYear(), now.getMonth(), 1)),
      };
    }
    case 'custom':
      return start && end ? { start, end } : { days: '30' };
  }
}

function rangeLabel(kind: RangeKind, start: string, end: string): string {
  switch (kind) {
    case '30d':
      return 'Last 30 days';
    case '90d':
      return 'Last 90 days';
    case 'prev-month':
      return 'Previous month';
    case 'custom':
      return start && end ? `${start} → ${end}` : 'Custom range';
  }
}

export const Route = createFileRoute('/cost')({
  component: CostDashboard,
});

function CostDashboard() {
  const [kind, setKind] = useState<RangeKind>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const params = useMemo(
    () => rangeToParams(kind, customStart, customEnd),
    [kind, customStart, customEnd],
  );
  const label = rangeLabel(kind, customStart, customEnd);
  const { data, isPending, isError, error } = useCostSnapshot(params);
  const role = useAuthStore(selectRole);
  const isSuper = role === 'superadmin';
  const orgCosts = useOrgCosts();
  const showOrgTable = isSuper;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Wallet}
        title="Cost & Usage"
        description="Account-wide AWS spend from Cost Explorer. Track burn rate and find idle resources to hibernate."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Cost' }]}
        actions={
          <Group gap="xs" align="center" wrap="wrap">
            <SegmentedControl
              size="xs"
              value={kind}
              onChange={(v) => setKind(v as RangeKind)}
              data={[
                { label: '30 days', value: '30d' },
                { label: '90 days', value: '90d' },
                { label: 'Prev month', value: 'prev-month' },
                { label: 'Custom', value: 'custom' },
              ]}
            />
            {kind === 'custom' && (
              <Group gap={6} wrap="nowrap">
                <TextInput
                  type="date"
                  size="xs"
                  aria-label="Start date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={(e) => setCustomStart(e.currentTarget.value)}
                />
                <Text size="xs" c="dimmed">
                  to
                </Text>
                <TextInput
                  type="date"
                  size="xs"
                  aria-label="End date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={(e) => setCustomEnd(e.currentTarget.value)}
                />
              </Group>
            )}
          </Group>
        }
      />

      {isError ? (
        <Alert color="red" icon={<AlertTriangle size={16} />} variant="light" title="Could not load cost">
          {(error as Error)?.message ?? 'Unknown error'}
        </Alert>
      ) : isPending || !data ? (
        <Skeleton height={120} radius="md" />
      ) : (
        <>
          {showOrgTable ? <OrgCostSection q={orgCosts} /> : null}
        <Section
            title="Spend overview"
            description="Live from AWS Cost Explorer (≈24–48h lag — today's usage settles tomorrow). Spend cap throttles new deploys at 100% but never terminates a running session — trainees finish what they started."
          >
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 gap-4 md:grid-cols-3"
            >
              <motion.div variants={cardItem}>
                <Card>
                  <Stack gap={6}>
                    <Group gap={6}>
                      <DollarSign size={14} color={colors.neon} />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        {label}
                      </Text>
                    </Group>
                    <Text className="font-mono" fw={700} size="30px" c={colors.neon}>
                      ${Math.max(0, data.monthToDate).toFixed(2)}
                    </Text>
                    <Group gap={4}>
                      <Progress
                        value={Math.min(100, (Math.max(0, data.monthToDate) / data.spendCap) * 100)}
                        color="neon"
                        radius="xl"
                        size="sm"
                        style={{ flex: 1 }}
                        aria-label="Spend cap usage"
                      />
                      <Text size="xs" c="dimmed" className="font-mono">
                        {Math.round((Math.max(0, data.monthToDate) / data.spendCap) * 100)}%
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Spend cap ${data.spendCap.toFixed(0)} / month
                    </Text>
                  </Stack>
                </Card>
              </motion.div>

              <motion.div variants={cardItem}>
                <Card>
                  <Stack gap={6}>
                    <Group gap={6}>
                      <TrendingUp size={14} color={colors.hudCyan} />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        Forecast month
                      </Text>
                    </Group>
                    <Text className="font-mono" fw={700} size="30px" c={colors.hudCyan}>
                      ${Math.max(0, data.forecastMonth).toFixed(2)}
                    </Text>
                    <Badge variant={data.forecastMonth > data.spendCap ? 'danger' : 'success'} size="xs">
                      {data.forecastMonth > data.spendCap ? 'over cap' : 'within cap'}
                    </Badge>
                  </Stack>
                </Card>
              </motion.div>

              <motion.div variants={cardItem}>
                <Card>
                  <Stack gap={6}>
                    <Group gap={6}>
                      <PieChart size={14} color={colors.hudAmber} />
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        Top service
                      </Text>
                    </Group>
                    <Text className="font-mono" fw={700} size="30px" c={colors.hudAmber}>
                      {data.byService[0]?.service ?? '—'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      ${data.byService[0]?.amount.toFixed(2) ?? '0.00'} · {label.toLowerCase()}
                    </Text>
                  </Stack>
                </Card>
              </motion.div>
            </motion.div>
          </Section>

          <Section title="Spend over time" description={`${label} · Cost Explorer · USD`}>
            <Card>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group gap="xs">
                    <TrendingUp size={16} color={colors.hudCyan} />
                    <Text fw={600} size="sm">
                      Spend — {label}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Cost Explorer · USD
                  </Text>
                </Group>
                <Suspense fallback={<Skeleton height={260} />}>
                  <CostCharts daily={data.dailyCost} byService={data.byService} />
                </Suspense>
              </Stack>
            </Card>
          </Section>
        </>
      )}
    </PageContainer>
  );
}

function OrgCostSection({ q }: { q: ReturnType<typeof useOrgCosts> }) {
  const cols: ReadonlyArray<TableColumn<OrgCostRow>> = [
    { key: 'name', header: 'Organization', cell: (o) => <Text size="sm" c="rgb(var(--c-txt-bright))">{o.name}</Text> },
    { key: 'billed', header: 'Billed MTD', align: 'right', cell: (o) => <Text size="sm" className="font-mono">${o.billedMtd.toFixed(2)}</Text> },
    { key: 'rate', header: 'Running $/hr', align: 'right', cell: (o) => <Text size="sm" className="font-mono">${o.runningHourly.toFixed(3)}</Text> },
    { key: 'accrued', header: 'Running accrued', align: 'right', cell: (o) => <Text size="sm" className="font-mono">${o.runningAccrued.toFixed(2)}</Text> },
    { key: 'inst', header: 'VMs', align: 'right', cell: (o) => <Text size="sm" className="font-mono">{o.runningInstances}</Text> },
  ];
  return (
    <Section title="Cost per organization" description="Actual AWS cost — labs + scenarios only, idle platform excluded.">
      <Card>
        {q.isPending ? <Skeleton height={120} /> : q.isError ? (
          <Text size="sm" c="red">Could not load per-org cost.</Text>
        ) : (
          <>
            <Table columns={cols} rows={(q.data?.items ?? []) as OrgCostRow[]} rowKey={(o) => o.id} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, gap: 16, flexWrap: 'wrap' }}>
              <Text size="xs" c="dimmed">{q.data?.note}</Text>
              <Text size="sm" fw={700} className="font-mono">
                Total: ${(q.data?.totals.billedMtd ?? 0).toFixed(2)} billed · ${(q.data?.totals.runningHourly ?? 0).toFixed(3)}/hr running
              </Text>
            </div>
          </>
        )}
      </Card>
    </Section>
  );
}

interface OrgCostRow { id: string; name: string; billedMtd: number; runningHourly: number; runningAccrued: number; runningInstances: number; }
